import { startOfWeek, format } from "date-fns";
import { saveAs } from "file-saver";
import JsZip from "jszip";
import { keyBy, orderBy } from "lodash";
import moment from "moment";
import { unparse } from "papaparse";

import { type SpendDataColumns_me_mmm_spendDataColumns_spendDataColumns } from "@north-beam/nb-client-side";
import {
  formatting,
  type BudgetMixBreakdownSpendDatum,
  type CurrencyCode,
  type FullBudgetMixResponse,
  type WeeklyBudgetMixDatum,
} from "@north-beam/nb-common";
import { formatCurrency } from "@north-beam/nb-common/formatting";

import { type HistoricalBudgetMixResponse } from "../../../budget-mix-card-container/card-container";
import { type Kpi } from "../../../client-setting/types";

const BLOB_TYPE = "text/plain;charset=utf-8";

interface WeeklyHistoricalSpendDatum {
  startDate: string;
  endDate: string;
  spendData: Record<string, { channel: string; spend: number }>;
}

interface WeeklyHistoricalSummaryDatum {
  startDate: string;
  endDate: string;
  kpiData: Record<string, number>;
  spend: number;
}

function getBMEndDate(dateString: string) {
  return moment(dateString).subtract(1, "days");
}

const generateSpendCsvBlob = (
  spendData: BudgetMixBreakdownSpendDatum[],
  totalSpend: number,
  code: CurrencyCode,
) => {
  const result = orderBy(spendData, [
    (e) => e.breakdowns.map(({ value }) => value).join(" / "),
    "asc",
  ]).map((data) => {
    const channel = data.breakdowns.map(({ value }) => value).join(" / ");
    const spend = formatCurrency(data.spend, { code });

    const mixCalc = data.spend / totalSpend;
    let mix = "";
    if (Number.isNaN(mixCalc)) {
      mix = "-%";
    } else {
      mix = formatting.formatPercentage(mixCalc, { precision: "exact" });
    }
    return [channel, spend, mix];
  });

  const spendCsv = unparse({
    fields: ["Channel", "Spend", "Mix"],
    data: result,
  });

  return new Blob([spendCsv], { type: BLOB_TYPE });
};

const generateKpiCsvBlob = (
  aggregated_data: FullBudgetMixResponse["data"]["aggregated_data"],
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const kpiMap = keyBy(kpis, "kpi_name");

  const kpiData = orderBy(
    Object.entries(aggregated_data.summary_data.kpi_data),
    [([kpiName]) => kpiMap[kpiName]?.friendly_name ?? kpiName, "asc"],
  ).map(([kpiName, { values }]) => {
    const isRevenue = kpiName.toLowerCase().includes("revenue");
    let format = (value: number) =>
      formatting.formatInteger(value, { precision: "exact" });
    if (isRevenue) {
      format = (value: number) => formatCurrency(value, { code });
    }

    return [
      kpiMap[kpiName]?.friendly_name ?? kpiName,
      format(values.low),
      format(values.median),
      format(values.high),
    ];
  });
  const kpiCsv = unparse({
    fields: ["KPI", "Low", "Median", "High"],
    data: kpiData,
  });

  return new Blob([kpiCsv], { type: BLOB_TYPE });
};

const generateHistoricalKpiCsvBlob = (
  kpiData: Record<string, number>,
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const kpiMap = keyBy(kpis, "kpi_name");

  const result = orderBy(Object.entries(kpiData), [
    ([kpiName]) => kpiMap[kpiName]?.friendly_name ?? kpiName,
    "asc",
  ]).map(([kpiName, value]) => {
    const isRevenue = kpiName.toLowerCase().includes("revenue");
    let format = (value: number) =>
      formatting.formatInteger(value, { precision: "exact" });
    if (isRevenue) {
      format = (value: number) => formatCurrency(value, { code });
    }

    return [kpiMap[kpiName]?.friendly_name ?? kpiName, format(value)];
  });
  const kpiCsv = unparse({
    fields: ["KPI", "Value"],
    data: result,
  });

  return new Blob([kpiCsv], { type: BLOB_TYPE });
};

const includeWeeklyKpiDataInZip = (
  zip: JsZip,
  weekly_data: WeeklyBudgetMixDatum[],
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const fields = weekly_data.map(
    ({ start_date, end_date }) =>
      `${moment(start_date).format("MMM DD")} - ${getBMEndDate(end_date).format(
        "MMM DD",
      )}`,
  );
  fields.unshift("");

  const kpiWeeklyData: any = {};
  kpis.forEach((kpi) => {
    const isRevenue = kpi.kpi_name.toLowerCase().includes("revenue");
    let format = (value: number) =>
      formatting.formatInteger(value, { precision: "exact" });
    if (isRevenue) {
      format = (value: number) => formatCurrency(value, { code });
    }

    const kpiWeeklySpend: any = ["Spend"];
    const kpiWeeklyLow: any = ["Low"];
    const kpiWeeklyMedian: any = ["Median"];
    const kpiWeeklyHigh: any = ["High"];
    weekly_data.forEach(({ summary_data }) => {
      const values = summary_data.kpi_data[kpi.kpi_name]?.values;
      kpiWeeklySpend.push(formatCurrency(summary_data.spend, { code }));
      kpiWeeklyLow.push(format(values?.low ?? NaN));
      kpiWeeklyMedian.push(format(values?.median ?? NaN));
      kpiWeeklyHigh.push(format(values?.high ?? NaN));
    });
    kpiWeeklyData[kpi.friendly_name] = [
      kpiWeeklySpend,
      kpiWeeklyLow,
      kpiWeeklyMedian,
      kpiWeeklyHigh,
    ];
  });

  Object.entries(kpiWeeklyData).forEach(([kpiName, data]) => {
    const kpiWeeklyCsv = unparse({ fields, data: data as any });
    zip.file(`${kpiName}.csv`, kpiWeeklyCsv);
  });
};

const includeWeeklyHistoricalKpiDataInZip = (
  zip: JsZip,
  weeklyData: WeeklyHistoricalSummaryDatum[],
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const fields = weeklyData.map(
    ({ startDate, endDate }) =>
      `${moment(startDate).format("MMM DD")} - ${moment(endDate).format(
        "MMM DD",
      )}`,
  );
  fields.unshift("");

  const kpiWeeklyData: any = {};
  kpis.forEach((kpi) => {
    const isRevenue = kpi.kpi_name.toLowerCase().includes("revenue");
    let format = (value: number) =>
      formatting.formatInteger(value, { precision: "exact" });
    if (isRevenue) {
      format = (value: number) => formatCurrency(value, { code });
    }

    const kpiWeeklySpend: any = ["Spend"];
    const kpiWeeklyValue: any = ["Value"];
    weeklyData.forEach((row) => {
      kpiWeeklySpend.push(formatCurrency(row.spend, { code }));
      kpiWeeklyValue.push(format(row.kpiData[kpi.kpi_name] ?? NaN));
    });
    kpiWeeklyData[kpi.friendly_name] = [kpiWeeklySpend, kpiWeeklyValue];
  });

  Object.entries(kpiWeeklyData).forEach(([kpiName, data]) => {
    const kpiWeeklyCsv = unparse({ fields, data: data as any });
    zip.file(`${kpiName}.csv`, kpiWeeklyCsv);
  });
};

const generateHistoricalWeeklySpendCsvString = (
  weeklyData: WeeklyHistoricalSpendDatum[],
  code: CurrencyCode,
) => {
  const data: any[] = [];

  weeklyData.forEach((row) => {
    const week = `${moment(row.startDate).format("MMM DD, YYYY")} - ${moment(
      row.endDate,
    ).format("MMM DD, YYYY")}`;

    Object.values(row.spendData).forEach((datum) => {
      const existing = data.find((e) => e.Channel === datum.channel);
      const spend = formatCurrency(datum.spend, { code });

      if (existing) {
        existing[week] = spend;
      } else {
        data.push({ Channel: datum.channel, [week]: spend });
      }
    });
  });

  return unparse(orderBy(data, ["Channel", "asc"]));
};

export const handleExportBudgetMix = async (
  { name, data: { aggregated_data, weekly_data } }: FullBudgetMixResponse,
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const zip = JsZip();

  const spendBlob = generateSpendCsvBlob(
    aggregated_data.spend_data,
    aggregated_data.summary_data.spend,
    code,
  );
  zip.file("Spend Summary.csv", spendBlob);

  const kpiBlob = generateKpiCsvBlob(aggregated_data, kpis, code);
  zip.file("KPIs Summary.csv", kpiBlob);

  includeWeeklyKpiDataInZip(zip, weekly_data, kpis, code);

  const zipFile = await zip.generateAsync({ type: "blob" });
  saveAs(zipFile, `${name}_budget_mix.zip`);
};

export const handleExportHistoricalBudgetMix = async (
  data: HistoricalBudgetMixResponse,
  kpis: Kpi[],
  code: CurrencyCode,
) => {
  const zip = JsZip();

  const spendBlob = generateSpendCsvBlob(
    data.data.spend_data,
    data.data.summary_data.spend,
    code,
  );
  zip.file("Spend Summary.csv", spendBlob);

  const kpiBlob = generateHistoricalKpiCsvBlob(
    data.data.summary_data.kpi_data,
    kpis,
    code,
  );
  zip.file("KPIs Summary.csv", kpiBlob);

  const weeklySpendData: WeeklyHistoricalSpendDatum[] = [];
  const weeklySummaryData: WeeklyHistoricalSummaryDatum[] = [];

  const firstDate = data.daily_data?.[0]?.date;
  const day = moment(firstDate).day();

  data.daily_data.forEach((row) => {
    const date = moment(row.date);

    const startOfCustomWeek = startOfWeek(date.toDate(), {
      weekStartsOn: day as any,
    });

    // make the start date and end date align with the Tuesday start
    const startDate = moment(startOfCustomWeek).format("YYYY-MM-DD");
    const endDate = moment(startOfCustomWeek)
      .add(6, "days")
      .format("YYYY-MM-DD");

    let weeklySpendDatum = weeklySpendData.find(
      (e) => e.startDate === startDate && e.endDate === endDate,
    );

    let weeklySummaryDatum = weeklySummaryData.find(
      (e) => e.startDate === startDate && e.endDate === endDate,
    );

    if (!weeklySpendDatum) {
      const spendData: Record<string, { channel: string; spend: number }> = {};

      row.spend_data.forEach((spendRow) => {
        spendData[spendRow.data_column_name] = {
          channel: spendRow.breakdowns.map((e) => e.value).join(" / "),
          spend: 0,
        };
      });

      weeklySpendDatum = {
        startDate,
        endDate,
        spendData,
      };

      weeklySpendData.push(weeklySpendDatum);
    }

    if (!weeklySummaryDatum) {
      const kpiData: Record<string, number> = {};

      kpis.forEach((kpi) => {
        kpiData[kpi.kpi_name] = 0;
      });

      weeklySummaryDatum = {
        startDate,
        endDate,
        spend: 0,
        kpiData,
      };

      weeklySummaryData.push(weeklySummaryDatum);
    }

    row.spend_data.forEach((spendRow) => {
      weeklySpendDatum!.spendData[spendRow.data_column_name].spend +=
        spendRow.spend;
    });

    weeklySummaryDatum.spend += row.summary_data.spend;
    Object.entries(row.summary_data.kpi_data).forEach(([kpi, value]) => {
      weeklySummaryDatum!.kpiData[kpi] += value as number;
    });
  });

  includeWeeklyHistoricalKpiDataInZip(zip, weeklySummaryData, kpis, code);

  const spendWeeklyBlob = generateHistoricalWeeklySpendCsvString(
    weeklySpendData,
    code,
  );
  zip.file("Spend Weekly Summary.csv", spendWeeklyBlob);

  const zipFile = await zip.generateAsync({ type: "blob" });
  saveAs(zipFile, "historical_budget_mix.zip");
};

export const handleExportScenario = async (
  { name, data: { weekly_data } }: FullBudgetMixResponse,
  spendDataColumns:
    | SpendDataColumns_me_mmm_spendDataColumns_spendDataColumns[]
    | undefined,
  code: CurrencyCode,
) => {
  const dataColumnById = spendDataColumns ? keyBy(spendDataColumns, "id") : {};
  const scenarioMap: Record<string, string[]> = {};
  weekly_data.forEach(({ spend_data }) => {
    spend_data.forEach((spend) => {
      const channel =
        dataColumnById[spend.data_column_name]?.prettyName ??
        spend.breakdowns.map(({ value }) => value).join(" / ");
      const spendValue = formatCurrency(spend.spend, { code });

      if (!scenarioMap[channel]) {
        scenarioMap[channel] = [];
      }

      scenarioMap[channel].push(spendValue);
    });
  });

  const scenarioData = Object.entries(scenarioMap).map(([channel, values]) => [
    channel,
    ...values,
  ]);

  const fields = weekly_data.map(
    ({ start_date, end_date }) =>
      `${moment(start_date).format("MMM DD, YYYY")} - ${getBMEndDate(
        end_date,
      ).format("MMM DD, YYYY")}`,
  );

  fields.unshift("Channel");

  const scenarioCsv = unparse({
    fields,
    data: orderBy(scenarioData, [(e) => e[0]], ["asc"]),
  });

  const scenarioBlob = new Blob([scenarioCsv], {
    type: BLOB_TYPE,
  });

  saveAs(scenarioBlob, `${name}_scenario.csv`);
};

export const handleExportHistoricalScenario = async (
  data: HistoricalBudgetMixResponse,
  periodStartDateString: string,
  code: CurrencyCode,
) => {
  const weeklySpendData: WeeklyHistoricalSpendDatum[] = [];
  const periodStartDate = moment(periodStartDateString).startOf("week");

  data.daily_data.forEach((row, index) => {
    const date = moment(periodStartDate).add(index, "days");
    const startDate = date.startOf("week").add(2, "days").format("YYYY-MM-DD");
    const endDate = moment(startDate).add(6, "days").format("YYYY-MM-DD");

    let weeklySpendDatum = weeklySpendData.find(
      (e) => e.startDate === startDate && e.endDate === endDate,
    );

    if (!weeklySpendDatum) {
      const spendData: Record<string, { channel: string; spend: number }> = {};

      row.spend_data.forEach((spendRow) => {
        spendData[spendRow.data_column_name] = {
          channel: spendRow.breakdowns.map((e) => e.value).join(" / "),
          spend: 0,
        };
      });

      weeklySpendDatum = {
        startDate,
        endDate,
        spendData,
      };

      weeklySpendData.push(weeklySpendDatum);
    }

    row.spend_data.forEach((spendRow) => {
      weeklySpendDatum!.spendData[spendRow.data_column_name].spend +=
        spendRow.spend;
    });
  });

  const spendWeeklyString = generateHistoricalWeeklySpendCsvString(
    weeklySpendData,
    code,
  );

  const scenarioBlob = new Blob([spendWeeklyString], {
    type: BLOB_TYPE,
  });
  saveAs(scenarioBlob, "historical_scenario.csv");
};

export const handleExportAdminCreationMetadata = (
  name: string,
  creationMetadata: Record<string, any>,
) => {
  const metadataBlob = new Blob([JSON.stringify(creationMetadata)], {
    type: BLOB_TYPE,
  });

  saveAs(metadataBlob, `${name}_creation_metadata.json`);
};
